import Box from 'components/shared/Box'
import Info from 'components/shared/Info'
import { RadioTileGroup } from 'components/shared/RadioTileGroup'
import Text from 'components/shared/Text'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { FormattedMessage } from 'react-intl'
import { getTimeSlot } from 'routes/Upsell/SpaCreator/helpers/dateSelector'
import SpaLoader from 'routes/Upsell/SpaCreator/SpaLoader'
import { useListCreatorSpaTreatmentsAvailableHourQuery } from 'store/api-slice/hooks'
import { useResolutions } from 'store/resolutions'
import { useApp } from 'store/steps'
import { TimeSelectorProps } from './TimeSelector.types'
import { GenericTechnicalError } from 'components/global/GenericTechnicalError'

export const TimeSelector = forwardRef((props: TimeSelectorProps, ref) => {
    const { language } = useApp()
    const { isMobile } = useResolutions()

    const {
        variantId,
        selectedDate,
        selectedTime,
        onDataFetched,
        onTimeSelect,
    } = props

    const { isFetching, isError, data, refetch } =
        useListCreatorSpaTreatmentsAvailableHourQuery(
            {
                _locale: language,
                startDate: selectedDate,
                productSpaTreatmentId: variantId,
            },
            {
                refetchOnMountOrArgChange: true,
            }
        )

    const isAnySlotAvailable = !!data?.items?.some(
        ({ isAvailable }) => isAvailable
    )

    useEffect(() => {
        onDataFetched(!isFetching && !isError && isAnySlotAvailable)
    }, [isFetching, isError, isAnySlotAvailable])

    useImperativeHandle(ref, () => ({
        refetchHours: () => refetch(),
    }))

    return (
        <Box width="100%" gap={[8, 16]}>
            <Text variant="heading5" color="grayscale_900">
                <FormattedMessage
                    id="upsells.wellness.choose_time"
                    defaultMessage="Wybierz godzinę"
                />
            </Text>
            {(!data || isFetching) && (
                <>
                    {isFetching && <SpaLoader />}
                    {isError && <GenericTechnicalError variant="warning" />}
                </>
            )}
            {!!data && !isFetching && (
                <>
                    {!!data?.items?.length && isAnySlotAvailable ? (
                        <RadioTileGroup
                            value={selectedTime}
                            columns={isMobile ? 2 : 5}
                            onChange={onTimeSelect}
                            data-testid="spa-time"
                        >
                            {data.items.map(
                                (
                                    { startDate, endDate, isAvailable },
                                    index
                                ) => {
                                    if (!startDate) return <></>

                                    return (
                                        <RadioTileGroup.SimpleItem
                                            key={`${startDate}${index}`}
                                            id={startDate}
                                            // TODO: figure out how to pass combined data in better way
                                            value={JSON.stringify({
                                                startTime: startDate,
                                                endTime: endDate,
                                            })}
                                            label={getTimeSlot(startDate)}
                                            isRadioMarkVisible={false}
                                            isDisabled={!isAvailable}
                                            width={['100%', 112]}
                                        />
                                    )
                                }
                            )}
                        </RadioTileGroup>
                    ) : (
                        <Info
                            color="info_warning"
                            align="center"
                            variantType="error"
                            hasNoBackground
                            hasBorder
                        >
                            <Text variant="bodySmall" color="grayscale_900">
                                <FormattedMessage
                                    id="upsells.wellness.no_timeslots"
                                    defaultMessage="W tym dniu nie mamy już wolnych miejsc na zabiegi. Wybierz inny dzień."
                                    values={{
                                        break: (chunks: string[]) => <br />,
                                    }}
                                />
                            </Text>
                        </Info>
                    )}
                </>
            )}
        </Box>
    )
})

TimeSelector.displayName = 'TimeSelector'
